import React, { useEffect, useMemo, useState } from 'react';
import {
    View,
    TouchableOpacity,
    RefreshControl,
    StyleSheet,
    FlatList,
    ScrollView,
    useWindowDimensions,
} from 'react-native';
import moment from 'moment';

import { appColors, useFontSize } from '../../../themes/appTheme';
import { appFonts } from 'zen-core-elements/src/shared/constants';
import CommonService from '../../../shared/services/commonService';
import CommonHeader from '../../../shared/commonComponents/commonHeader';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation';
import { useIsFocused } from '@react-navigation/native';

import {
    ZenBadge,
    ZenDateTimePicker,
    ZenEmptyComponent,
    ZenIcon,
    ZenLoader,
    ZenText,
} from 'zen-core-elements';

import {
    FlattenedReportType,
    offers,
    offersReport,
    setFilterData,
    setOffer,
} from '../../../redux/slices/offerReportSlice';
import useBackHandler from '../../../shared/hooks/useBackHandler';
import { showApiError } from '../../../shared/utils/handleApiError';

const OfferReportPage = () => {
    const limit = 10;
    const { width, height } = useWindowDimensions();
    const isLandscape = useMemo(() => width > height, [width, height]);
    const dynamicFontSize = useFontSize(isLandscape);

    const dispatch = useAppDispatch();
    const navigation = useAppNavigation();
    useBackHandler();
    const route = useAppRoute('OfferReport');
    const isFocused = useIsFocused();

    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

    const { offer, selectedOffer } = useAppSelector((state) => state?.offerReport);
    const currentOffer = selectedOffer ? offer[selectedOffer] : undefined;

    const [datePickerVisible, setDatePickerVisible] = useState<{
        visibility: boolean;
        current: 'from' | 'to' | null;
    }>({ visibility: false, current: null });

    const [footerLoad, setFooterLoad] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<boolean>(false);
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    const typesOfOffer: { label: string; value: offers; icon: string; color: string }[] = useMemo(
        () => [
            { label: 'Manual Offers', value: 'manualOffer', icon: 'ticket-percent-outline', color: '#4F46E5' },
            { label: 'Automatic Offers', value: 'automaticOffer', icon: 'lightning-bolt-outline', color: '#F59E0B' },
            // { label: 'Loyalty Offers', value: 'loyaltyOffer', icon: 'star-circle-outline', color: '#EC4899' },
            { label: 'Additional Offers', value: 'additionalOffer', icon: 'plus-circle-outline', color: '#10B981' },
            { label: 'BuyX GetY Offers', value: 'buyXGetYOffer', icon: 'gift-outline', color: '#8B5CF6' },
            { label: 'PayX GetFree Offers', value: 'payXGetFreeOffer', icon: 'tag-heart-outline', color: '#06B6D4' },
        ],
        []
    );

    const currentTypeMeta = useMemo(() => {
        return typesOfOffer.find((t) => t.value === selectedOffer) || typesOfOffer[0];
    }, [selectedOffer, typesOfOffer]);

    const routeFrom = route?.params?.beginDate;
    const routeTo = route?.params?.endDate;

    useEffect(() => {
        if (!isFocused) return;

        const targetFrom = routeFrom || currentOffer?.filterData?.from || moment().format('YYYY-MM-DD');
        const targetTo = routeTo || currentOffer?.filterData?.to || moment().format('YYYY-MM-DD');

        const isDateDifferent =
            currentOffer?.filterData?.from !== targetFrom ||
            currentOffer?.filterData?.to !== targetTo;

        if (isDateDifferent || !currentOffer?.value?.length) {
            dispatch(setFilterData({ from: targetFrom, to: targetTo }));
            getReport({ forceTrigger: true, offset: 0 });
        }
    }, [isFocused, selectedOffer, routeFrom, routeTo]);

    const getReport = ({ forceTrigger, offset }: { offset?: number; forceTrigger?: boolean }) => {
        if (
            (!offer?.[selectedOffer]?.value?.length && offer?.[selectedOffer]?.filterData?.from == null) ||
            forceTrigger
        ) {
            const data = {
                limit: limit,
                offset: offset || 0,
                locationId: locationId
            };
            dispatch(offersReport({ data }))
                .unwrap()
                .catch(showApiError).finally(() => {
                    setRefresh(false);
                    setFooterLoad(false);
                });
        }
    };

    const listTitle = useMemo(() => {
        if (selectedOffer === 'manualOffer') {
            return 'Promo Code';
        } else if (
            selectedOffer === 'automaticOffer' ||
            selectedOffer === 'buyXGetYOffer' ||
            selectedOffer === 'payXGetFreeOffer'
        ) {
            return 'Offer Title';
        } else {
            return 'Date';
        }
    }, [selectedOffer]);

    const onEndReached = () => {
        const offsetValue = currentOffer?.offset || 0;
        const nextOffsetValue = offsetValue + limit;
        if (!footerLoad && (currentOffer?.count || 0) > nextOffsetValue) {
            setFooterLoad(true);
            getReport({ forceTrigger: true, offset: nextOffsetValue });
        }
    };

    const onRefresh = () => {
        setRefresh(true);
        const targetFrom = routeFrom || currentOffer?.filterData?.from || moment().format('YYYY-MM-DD');
        const targetTo = routeTo || currentOffer?.filterData?.to || moment().format('YYYY-MM-DD');
        dispatch(setFilterData({ from: targetFrom, to: targetTo }));
        getReport({ forceTrigger: true, offset: 0 });
    };

    // Calculate Summary KPIs
    const totalOfferAmount = useMemo(() => {
        if (
            currentOffer?.totalOfferAmount !== undefined &&
            currentOffer?.totalOfferAmount !== null &&
            !isNaN(currentOffer.totalOfferAmount)
        ) {
            return currentOffer.totalOfferAmount;
        }
        return currentOffer?.value?.reduce((acc, item) => acc + (Number(item?.totalAmountPerCodeOrDate) || 0), 0) || 0;
    }, [currentOffer]);

    const totalOrdersCount = useMemo(() => {
        return currentOffer?.value?.reduce((acc, item) => acc + (Number(item?.orderCount) || 0), 0) || 0;
    }, [currentOffer?.value]);

    const totalRecordsCount = Number(currentOffer?.count ?? currentOffer?.value?.length ?? 0);
    const avgDiscountPerOrder = totalOrdersCount > 0 && !isNaN(totalOfferAmount) ? totalOfferAmount / totalOrdersCount : 0;

    const statsData = [
        {
            id: '1',
            title: 'Total Discount',
            value: CommonService.currencyStandard(currencySymbol, totalOfferAmount.toString()),
            iconName: 'brightness-percent',
            color: '#10B981',
        },
        {
            id: '2',
            title: 'Orders Discounted',
            value: String(totalOrdersCount),
            iconName: 'shopping-outline',
            color: '#3B82F6',
        },
        {
            id: '3',
            title: 'Total Offers / Codes',
            value: String(totalRecordsCount),
            iconName: 'ticket-percent-outline',
            color: '#8B5CF6',
        },
        {
            id: '4',
            title: 'Avg Discount / Order',
            value: CommonService.currencyStandard(currencySymbol, avgDiscountPerOrder.toFixed(2)),
            iconName: 'calculator-variant-outline',
            color: '#F59E0B',
        },
    ];

    const StatCard = ({
        title,
        value,
        iconName,
        color,
    }: {
        title: string;
        value: string;
        iconName: string;
        color: string;
    }) => (
        <View style={[styles.card, !isLandscape && styles.cardPortrait]}>
            <View
                style={[
                    styles.iconContainer,
                    !isLandscape && styles.iconContainerPortrait,
                    { backgroundColor: color + '15' },
                ]}
            >
                <ZenIcon type="material-community" name={iconName} size={isLandscape ? 24 : 20} color={color} />
            </View>
            <View style={{ flex: 1 }}>
                <ZenText
                    content={title}
                    size={dynamicFontSize.errorFontSize}
                    color={appColors.lightDark}
                    style={{ marginBottom: 3 }}
                    numberOfLines={1}
                />
                <ZenText
                    content={value}
                    bold
                    color={appColors.dark}
                    size={dynamicFontSize.largerFontSize}
                    numberOfLines={1}
                />
            </View>
        </View>
    );

    const renderTableHeader = () => (
        <View style={styles.tableHeader}>
            <ZenText
                content={listTitle.toUpperCase()}
                size={dynamicFontSize.errorFontSize}
                color={appColors.lightDark}
                bold
                style={[styles.headerText, { flex: 2, paddingLeft: 12 }]}
            />
            <ZenText
                content="ORDERS"
                size={dynamicFontSize.errorFontSize}
                color={appColors.lightDark}
                bold
                style={[styles.headerText, { flex: 1, textAlign: 'center' }]}
            />
            <ZenText
                content="TOTAL DISCOUNT"
                size={dynamicFontSize.errorFontSize}
                color={appColors.lightDark}
                bold
                style={[styles.headerText, { flex: 1.5, textAlign: 'right', paddingRight: 12 }]}
            />
        </View>
    );

    const renderItem = ({ item }: { item: FlattenedReportType }) => {
        if (!isLandscape) {
            return (
                <View style={styles.mobileCard}>
                    <View style={styles.mobileCardHeader}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: 8 }}>
                            <View style={styles.mobileRowIconCircle}>
                                <ZenIcon
                                    type="material-community"
                                    name={currentTypeMeta?.icon || 'tag-outline'}
                                    size={16}
                                    color={appColors.primary}
                                />
                            </View>
                            <ZenText
                                content={item?.title ?? 'N/A'}
                                bold
                                size={dynamicFontSize.smallHeaderFontSize}
                                color={appColors.dark}
                                style={{ marginLeft: 8, flex: 1 }}
                                numberOfLines={1}
                                ellipsizeMode="tail"
                            />
                        </View>
                        <ZenBadge
                            badgeName={`${item?.orderCount ?? 0} Orders`}
                            badgeTextSize={dynamicFontSize.badgeFontSize}
                            badgeTextColor={item?.orderCount > 0 ? appColors.primary : appColors.disabledGray500}
                            badgeColor={item?.orderCount > 0 ? appColors.lightPrimary : '#F1F5F9'}
                        />
                    </View>

                    <View style={styles.mobileCardBody}>
                        <View style={styles.mobileCardRow}>
                            <View style={styles.mobileCardCol}>
                                <ZenText
                                    content="Total Discount"
                                    size={dynamicFontSize.errorFontSize}
                                    color={appColors.lightDark}
                                    style={{ marginBottom: 2 }}
                                />
                                <ZenText
                                    content={CommonService.currencyStandard(
                                        currencySymbol,
                                        item?.totalAmountPerCodeOrDate?.toString() || '0'
                                    )}
                                    bold
                                    size={dynamicFontSize.smallHeaderFontSize}
                                    color={appColors.primary}
                                />
                            </View>
                            <View style={[styles.mobileCardCol, { alignItems: 'flex-end' }]}>
                                <ZenText
                                    content="Orders Applied"
                                    size={dynamicFontSize.errorFontSize}
                                    color={appColors.lightDark}
                                    style={{ marginBottom: 2 }}
                                />
                                <ZenText
                                    content={String(item?.orderCount ?? 0)}
                                    bold
                                    size={dynamicFontSize.smallHeaderFontSize}
                                    color={appColors.dark}
                                />
                            </View>
                        </View>
                    </View>
                </View>
            );
        }

        return (
            <View style={styles.tableRow}>
                <View style={{ flex: 2, flexDirection: 'row', alignItems: 'center', paddingLeft: 12 }}>
                    <View style={styles.rowIconCircle}>
                        <ZenIcon
                            type="material-community"
                            name={currentTypeMeta?.icon || 'tag-outline'}
                            size={18}
                            color={appColors.primary}
                        />
                    </View>
                    <ZenText
                        content={item?.title ?? 'N/A'}
                        bold
                        size={dynamicFontSize.smallHeaderFontSize}
                        color={appColors.dark}
                        style={{ marginLeft: 10, flex: 1 }}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                    />
                </View>
                <View style={{ flex: 1, alignItems: 'center' }}>
                    <ZenBadge
                        badgeName={`${item?.orderCount ?? 0} Orders`}
                        badgeTextSize={dynamicFontSize.badgeFontSize}
                        badgeTextColor={item?.orderCount > 0 ? appColors.primary : appColors.disabledGray500}
                        badgeColor={item?.orderCount > 0 ? appColors.lightPrimary : '#F1F5F9'}
                    />
                </View>
                <ZenText
                    content={CommonService.currencyStandard(
                        currencySymbol,
                        item?.totalAmountPerCodeOrDate?.toString() || '0'
                    )}
                    bold
                    size={dynamicFontSize.smallHeaderFontSize}
                    color={appColors.primary}
                    style={{ flex: 1.5, textAlign: 'right', paddingRight: 12 }}
                />
            </View>
        );
    };

    const ListHeader = () => (
        <View style={styles.headerSection}>
            {/* KPI Stat Cards Grid */}
            <FlatList
                data={statsData}
                renderItem={({ item }) => (
                    <StatCard
                        title={item?.title}
                        value={item?.value}
                        iconName={item?.iconName}
                        color={item?.color}
                    />
                )}
                keyExtractor={(item) => item.id}
                numColumns={isLandscape ? 4 : 2}
                scrollEnabled={false}
                style={styles.statsGrid}
            />

            {/* Table Header for Landscape */}
            {(currentOffer?.value?.length ?? 0) > 0 && (
                <View style={{ marginTop: 10 }}>
                    <View style={styles.tableTitleRow}>
                        <ZenText
                            content="Offer Details"
                            size={dynamicFontSize.largerFontSize}
                            color={appColors.dark}
                            bold
                        />
                        <ZenBadge
                            badgeName={`${currentOffer?.value?.length ?? 0} Records`}
                            badgeTextSize={dynamicFontSize.badgeFontSize}
                            badgeTextColor={appColors.primary}
                            badgeColor={appColors.lightPrimary}
                        />
                    </View>
                    {isLandscape && renderTableHeader()}
                </View>
            )}
        </View>
    );

    return (
        <View style={styles.container}>
            <CommonHeader
                headerName="Offer Report"
                backIcon={true}
                backIconOnpress={() => {
                    navigation.goBack();
                }}
            />

            {/* Top Control Bar: Date Range Filter */}
            <View style={styles.topFilterBar}>
                <View style={styles.unifiedBar}>
                    <TouchableOpacity
                        style={styles.dateSegment}
                        onPress={() => setDatePickerVisible({ visibility: true, current: 'from' })}
                        activeOpacity={0.7}
                    >
                        <ZenIcon type="material-community" name="calendar-range" size={18} color={appColors.primary} />
                        <ZenText
                            content={
                                currentOffer?.filterData?.from
                                    ? moment(currentOffer?.filterData?.from).format('DD MMM, YYYY')
                                    : 'Select From'
                            }
                            size={dynamicFontSize.tinyFontSize}
                            color={appColors.primary}
                        />
                    </TouchableOpacity>

                    <View style={styles.verticalDivider} />

                    <TouchableOpacity
                        style={styles.dateSegment}
                        onPress={() => setDatePickerVisible({ visibility: true, current: 'to' })}
                        activeOpacity={0.7}
                    >
                        <ZenText
                            content={
                                currentOffer?.filterData?.to
                                    ? moment(currentOffer?.filterData?.to).format('DD MMM, YYYY')
                                    : 'Select To'
                            }
                            size={dynamicFontSize.tinyFontSize}
                            color={appColors.primary}
                        />
                        <ZenIcon type="material-community" name="chevron-down" size={16} color={appColors.primary} />
                    </TouchableOpacity>
                </View>
            </View>

            {/* Offer Type Horizontal Pill Selector */}
            <View style={styles.typeSelectorContainer}>
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.typeSelectorContent}
                >
                    {typesOfOffer.map((item) => {
                        const isSelected = selectedOffer === item.value;
                        return (
                            <TouchableOpacity
                                key={item.value}
                                style={[
                                    styles.typePill,
                                    isSelected && styles.typePillActive,
                                ]}
                                onPress={() => {
                                    if (selectedOffer !== item.value) {
                                        dispatch(setOffer(item.value));
                                    }
                                }}
                                activeOpacity={0.75}
                            >
                                <ZenIcon
                                    type="material-community"
                                    name={item.icon}
                                    size={18}
                                    color={isSelected ? appColors.light : item.color}
                                />
                                <ZenText
                                    content={item.label}
                                    bold={isSelected}
                                    size={dynamicFontSize.smallHeaderFontSize}
                                    color={isSelected ? appColors.light : appColors.dark}
                                    style={{ marginLeft: 6 }}
                                />
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            </View>

            {/* Date Time Picker Modal */}
            {datePickerVisible?.visibility && (
                <ZenDateTimePicker
                    date={
                        datePickerVisible?.current === 'from'
                            ? currentOffer?.filterData?.from
                                ? new Date(currentOffer?.filterData?.from)
                                : new Date()
                            : currentOffer?.filterData?.to
                                ? new Date(currentOffer?.filterData?.to)
                                : new Date()
                    }
                    mode="date"
                    open={datePickerVisible?.visibility}
                    maximumDate={
                        datePickerVisible?.current === 'from' && currentOffer?.filterData?.to
                            ? new Date(currentOffer?.filterData?.to)
                            : new Date()
                    }
                    minimumDate={
                        datePickerVisible?.current !== 'from' && currentOffer?.filterData?.from
                            ? new Date(currentOffer?.filterData?.from)
                            : undefined
                    }
                    onConfirm={(data) => {
                        if (datePickerVisible?.current === 'from') {
                            const newFrom = moment(data).format('YYYY-MM-DD');
                            const newTo = currentOffer?.filterData?.to || routeTo || moment(new Date()).format('YYYY-MM-DD');
                            dispatch(setFilterData({ from: newFrom, to: newTo }));
                            setDatePickerVisible({ current: null, visibility: false });
                            getReport({ forceTrigger: true, offset: 0 });
                        } else {
                            const newTo = moment(data).format('YYYY-MM-DD');
                            const newFrom = currentOffer?.filterData?.from || routeFrom || moment(new Date()).format('YYYY-MM-DD');
                            dispatch(setFilterData({ from: newFrom, to: newTo }));
                            setDatePickerVisible({ current: null, visibility: false });
                            getReport({ forceTrigger: true, offset: 0 });
                        }
                    }}
                    onCancel={() => {
                        setDatePickerVisible({ visibility: false, current: null });
                    }}
                />
            )}

            {/* Report Content List */}
            <View style={styles.contentContainer}>
                {currentOffer?.loading && !refresh && (!currentOffer?.offset || !currentOffer?.value?.length) ? (
                    <ZenLoader
                        lottieType="Local"
                        lottie={require('../../../assets/infinityLoader.json')}
                    />
                ) : (
                    <FlatList
                        data={currentOffer?.value || []}
                        keyExtractor={(item, index) => `${item.title}-${index}`}
                        renderItem={renderItem}
                        showsVerticalScrollIndicator={false}
                        ListHeaderComponent={ListHeader}
                        contentContainerStyle={[
                            styles.listContent,
                            { flexGrow: (currentOffer?.value?.length ?? 0) > 0 ? undefined : 1 },
                        ]}
                        ListEmptyComponent={
                            <ZenEmptyComponent />
                        }
                        onEndReached={onEndReached}
                        onEndReachedThreshold={0.4}
                        ListFooterComponent={
                            footerLoad ? (
                                <View style={styles.footerLoader}>
                                    <ZenLoader size="small" color={appColors.primary} />
                                </View>
                            ) : null
                        }
                        refreshControl={
                            <RefreshControl
                                refreshing={refresh}
                                onRefresh={onRefresh}
                                colors={[appColors.primary]}
                                tintColor={appColors.primary}
                            />
                        }
                    />
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: appColors.lightBackgroundColor,
    },
    topFilterBar: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingHorizontal: 15,
        marginTop: 12,
        marginBottom: 6,
    },
    unifiedBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFF',
        borderRadius: 16,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 6,
        elevation: 1,
    },
    dateSegment: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        gap: 6,
    },
    verticalDivider: {
        width: 1.5,
        height: 20,
        backgroundColor: '#E2E8F0',
        marginHorizontal: 4,
    },

    // Horizontal Offer Types Selector
    typeSelectorContainer: {
        paddingVertical: 8,
    },
    typeSelectorContent: {
        paddingHorizontal: 15,
        gap: 10,
    },
    typePill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFF',
        paddingVertical: 9,
        paddingHorizontal: 14,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 4,
        elevation: 1,
    },
    typePillActive: {
        backgroundColor: appColors.primary,
        borderColor: appColors.primary,
        shadowColor: appColors.primary,
        shadowOpacity: 0.2,
        shadowRadius: 6,
        elevation: 2,
    },

    // Content container & List
    contentContainer: {
        flex: 1,
        paddingHorizontal: 15,
    },
    listContent: {
        paddingBottom: 25,
    },
    headerSection: {
        marginBottom: 8,
    },

    // Stat Cards
    statsGrid: {
        marginHorizontal: -6,
        marginBottom: 10,
    },
    card: {
        flex: 1,
        minWidth: 160,
        margin: 6,
        backgroundColor: '#FFF',
        padding: 16,
        borderRadius: 18,
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#F1F5F9',
        shadowColor: '#000',
        shadowOpacity: 0.04,
        shadowRadius: 8,
        elevation: 2,
    },
    cardPortrait: {
        minWidth: 140,
        padding: 12,
        margin: 5,
        borderRadius: 14,
    },
    iconContainer: {
        padding: 12,
        borderRadius: 14,
        marginRight: 12,
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconContainerPortrait: {
        padding: 9,
        borderRadius: 10,
        marginRight: 10,
    },

    // Table Styles (Landscape)
    tableTitleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        paddingHorizontal: 4,
    },
    tableHeader: {
        flexDirection: 'row',
        paddingVertical: 14,
        paddingHorizontal: 16,
        backgroundColor: '#FFF',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomWidth: 1,
        borderColor: '#F1F5F9',
        alignItems: 'center',
    },
    headerText: {
        letterSpacing: 0.5,
        fontFamily: appFonts.bold,
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 16,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderColor: '#F8FAFC',
        alignItems: 'center',
        backgroundColor: '#FFF',
    },
    rowIconCircle: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: appColors.lightPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },

    // Mobile Card Styles (Portrait)
    mobileCard: {
        backgroundColor: '#FFF',
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        marginBottom: 10,
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 6,
        elevation: 1,
    },
    mobileCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#F8FAFC',
        paddingBottom: 10,
        marginBottom: 10,
    },
    mobileRowIconCircle: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: appColors.lightPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    mobileCardBody: {
        flexDirection: 'column',
    },
    mobileCardRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    mobileCardCol: {
        flex: 1,
        alignItems: 'flex-start',
    },
    footerLoader: {
        paddingVertical: 14,
    },
});

export default OfferReportPage;